import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Truck,
  Map,
  Fuel,
  Menu,
  LogOut,
} from "lucide-react";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";

const TABS = [
  { to: "/lm", label: "Fleet", icon: Truck, end: true, testid: "lm-tab-fleet" },
  { to: "/lm/status", label: "Status", icon: Map, testid: "lm-tab-status" },
  { to: "/lm/fuel", label: "Fuel", icon: Fuel, testid: "lm-tab-fuel", badgeKey: "pendingFuel" },
  { to: "/lm/more", label: "More", icon: Menu, testid: "lm-tab-more" },
];

function LMInner({ children }) {
  const [pendingFuel, setPendingFuel] = useState(0);
  // Lightweight poll for pending-fuel count so the badge stays fresh.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/fuel/refills", { params: { status: "pending", limit: 200 } });
        if (cancelled) return;
        setPendingFuel((data || []).length);
      } catch {
        /* non-blocking */
      }
    };
    tick();
    const t = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const badges = { pendingFuel };
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="lm-shell">
      {/* Top Bar */}
      <header className="sticky top-0 z-30 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white px-4 py-3 shadow-lg flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shadow-inner overflow-hidden p-1 flex-shrink-0">
            <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-bravura-200">Bravura Trucking</div>
            <div className="text-sm font-semibold truncate">
              {user?.name || user?.email || "Logistics Manager"}
            </div>
          </div>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition flex-shrink-0"
          data-testid="lm-logout-btn"
          title="Logout"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 px-4 pt-4 pb-24 max-w-2xl w-full mx-auto">
        {children}
      </main>

      {/* PWA Install prompt */}
      <InstallPrompt />

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]">
        <div className="max-w-2xl mx-auto grid grid-cols-4">
          {TABS.map((t) => {
            const badge = t.badgeKey ? badges[t.badgeKey] : 0;
            return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 gap-1 text-[11px] font-medium transition ${
                  isActive ? "text-bravura" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`relative h-9 w-9 rounded-xl flex items-center justify-center transition ${
                      isActive ? "bg-bravura-100 ring-2 ring-bravura-300" : "bg-transparent"
                    }`}
                  >
                    <t.icon className="h-5 w-5" />
                    {badge > 0 && (
                      <span
                        data-testid={`${t.testid}-badge`}
                        className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1"
                      >
                        {badge > 9 ? "9+" : badge}
                      </span>
                    )}
                  </div>
                  {t.label}
                </>
              )}
            </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default function LMShell({ children }) {
  return (
    <ProtectedRoute roles={["super_admin", "logistics_manager", "ea"]}>
      <LMInner>{children}</LMInner>
    </ProtectedRoute>
  );
}
